(() => {
  const DISCOUNT_CODE_KEY = 'cart-discount:code';
  const CACHED_PERCENT_KEY = 'welcome-discount:percent';
  const CACHED_CODE_KEY = 'welcome-discount:code';
  let panelWasOpen = false;
  let discountRevision = 0;
  let discountMutation = Promise.resolve();

  // All discount writers share one transaction. A stale reconcile must never
  // erase a code that a newer transaction just accepted.
  function changeDiscount(task) {
    discountRevision += 1;
    const result = discountMutation.then(task);
    discountMutation = result.catch(() => {});
    return result;
  }

  async function applyCode(cart, code) {
    const previous = (cart.discount_codes || [])
      .filter((d) => d.applicable !== false).map((d) => d.code);
    const combined = [...previous.filter((c) => c.toLowerCase() !== code.toLowerCase()), code];
    let updated = await postCartUpdate({ discount: combined.join(',') });
    if (isCodeApplied(updated, code) || combined.length === 1) return updated;
    // A valid replacement cannot combine with an old order code. Shopify's
    // generic applicable=false cannot distinguish that from an invalid code.
    updated = await postCartUpdate({ discount: code });
    if (!isCodeApplied(updated, code)) {
      // Do not make an invalid attempt cost the buyer their existing discount.
      await postCartUpdate({ discount: previous.join(',') });
    }
    return updated;
  }

  function isPanelOpen(el) {
    const toggle = el?.querySelector?.('.cart-discount__toggle');
    const panel = el?.querySelector?.('.cart-discount__panel');
    return toggle?.getAttribute('aria-expanded') === 'true' && panel && !panel.hidden;
  }

  function capturePanelOpenState() {
    const el = document.querySelector('cart-discount');
    panelWasOpen = isPanelOpen(el);
  }

  function getRoot() {
    return window.Shopify?.routes?.root || '/';
  }

  async function postCartUpdate(body) {
    const res = await fetch(`${getRoot()}cart/update.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  }

  // Shopify AJAX Cart API returns either:
  //   - New format: cart_level_discount_applications + discount_codes
  //   - Old format: discount_applications (flat array)
  // These helpers normalise both so the rest of the code doesn't care.

  function getAppliedDiscountTags(cart) {
    const tags = [];
    const seen = new Set();

    const addTag = (title, removable) => {
      const trimmed = (title || '').trim();
      if (!trimmed) return;
      const key = trimmed.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      tags.push({ title: trimmed, removable });
    };

    if (Array.isArray(cart.discount_codes)) {
      for (const d of cart.discount_codes) {
        if (d.applicable === false) continue;
        addTag(d.code, true);
      }
    }

    if (Array.isArray(cart.cart_level_discount_applications)) {
      for (const entry of cart.cart_level_discount_applications) {
        const app = normalizeDiscountApplication(entry);
        if (!app?.title || app.type === 'discount_code') continue;
        addTag(app.title, false);
      }
    }

    for (const item of cart.items || []) {
      for (const alloc of item.line_level_discount_allocations || []) {
        const app = alloc.discount_application;
        if (!app?.title || app.type === 'discount_code') continue;
        addTag(app.title, false);
      }
    }

    if (tags.length === 0 && Array.isArray(cart.discount_applications)) {
      for (const d of cart.discount_applications) {
        addTag(d.title, d.type === 'discount_code');
      }
    }

    return tags;
  }

  // Back-compat alias
  function getAppliedCodeTags(cart) {
    return getAppliedDiscountTags(cart);
  }

  function normalizeDiscountApplication(entry) {
    return entry?.discount_application || entry || null;
  }

  function getPercentageApp(cart) {
    if (Array.isArray(cart.cart_level_discount_applications)) {
      for (const entry of cart.cart_level_discount_applications) {
        const app = normalizeDiscountApplication(entry);
        if (app?.value_type === 'percentage') return app;
      }
      return null;
    }
    return (cart.discount_applications || []).find((d) => d.value_type === 'percentage') || null;
  }

  function isCodeApplied(cart, code) {
    const lower = code.toLowerCase();
    if (Array.isArray(cart.discount_codes)) {
      return cart.discount_codes.some((d) => d.code.toLowerCase() === lower && d.applicable !== false);
    }
    return (cart.discount_applications || []).some(
      (d) => d.type === 'discount_code' && d.title.toLowerCase() === lower
    );
  }

  function dispatchCartRefresh() {
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
  }

  function notifyCodeChange() {
    window.dispatchEvent(new CustomEvent('welcome-discount:code-changed'));
  }

  function clearWelcomeDiscountCache() {
    localStorage.removeItem(CACHED_PERCENT_KEY);
    localStorage.removeItem(CACHED_CODE_KEY);
  }

  function clearStoredDiscountCode() {
    localStorage.removeItem(DISCOUNT_CODE_KEY);
    clearWelcomeDiscountCache();
  }

  function clearDiscountCookie() {
    document.cookie = 'discount_code=; path=/; Max-Age=0; SameSite=Lax';
  }

  function getCookieDiscountCode() {
    const cookieMatch = document.cookie.match(/(?:^|;\s*)discount_code=([^;]+)/i);
    return cookieMatch?.[1]?.trim().toLowerCase() || '';
  }

  function getPercentageCodeFromCart(cart) {
    const pctApp = getPercentageApp(cart);
    if (!pctApp) return '';

    const applicable = (cart.discount_codes || []).filter((d) => d.applicable !== false);
    const storedCode = localStorage.getItem(DISCOUNT_CODE_KEY)?.trim().toLowerCase() || '';

    if (storedCode && applicable.some((d) => d.code.toLowerCase() === storedCode)) {
      return storedCode;
    }

    const title = (pctApp.title || '').trim().toLowerCase();
    const byTitle = applicable.find((d) => d.code.toLowerCase() === title);
    if (byTitle) return byTitle.code.toLowerCase();

    if (applicable.length === 1) return applicable[0].code.toLowerCase();

    return title || applicable[0]?.code?.toLowerCase() || '';
  }

  // A code from outside (a Klaviyo mail link, the popup) must not end BF prices on the whole site.
  // The BF code goes back on the cart, once per cart size so a cart that cannot take it is left alone.
  // Only the customer's own field and remove button replace a BF code; they write storage themselves.
  let bfRestoreTried = '';
  function bfCodeOutranked(cart, storedCode) {
    if (!window.bfInsider?.isCode?.(storedCode)) return false;
    const applied = (cart.discount_codes || [])
      .filter((d) => d.applicable !== false)
      .map((d) => d.code.toLowerCase());
    return applied.length > 0 && !applied.includes(storedCode);
  }

  async function restoreBfCode(cart, storedCode) {
    const attempt = `${storedCode}|${cart.item_count}`;
    if (attempt === bfRestoreTried) return;
    bfRestoreTried = attempt;
    await applyStoredDiscountToCart({ code: storedCode });
  }

  async function reconcileDiscountState() {
    const revision = discountRevision;
    await discountMutation;
    try {
      const cart = await fetch(`${getRoot()}cart.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      }).then((r) => r.json());
      if (revision !== discountRevision) return;

      const pctApp = getPercentageApp(cart);
      const storedCode = localStorage.getItem(DISCOUNT_CODE_KEY)?.trim().toLowerCase() || '';
      const cookieCode = getCookieDiscountCode();
      const hadCachedPercent = Boolean(localStorage.getItem(CACHED_PERCENT_KEY));
      let changed = false;

      if (bfCodeOutranked(cart, storedCode)) {
        await restoreBfCode(cart, storedCode);
        return;
      }

      if (!pctApp) {
        const applicableCodes = (cart.discount_codes || [])
          .filter((d) => d.applicable !== false)
          .map((d) => d.code.toLowerCase());
        // A BF code only applies to products with their own BF rate, so a cart without one reports
        // applicable=false for a valid code. Clearing it would end BF prices on the whole site.
        const isWaitingBfCode = (code) =>
          applicableCodes.length === 0 && Boolean(window.bfInsider?.isCode?.(code));

        if (storedCode && applicableCodes.length > 0 && !applicableCodes.includes(storedCode)) {
          clearStoredDiscountCode();
          changed = true;
        } else if (storedCode && applicableCodes.length === 0 && cart.item_count > 0 && !isWaitingBfCode(storedCode)) {
          // 🚨 `cart.item_count > 0` is load-bearing, not defensive. An empty cart cannot carry a
          // discount code at all, so `discount_codes` is always [] — without this guard, a code the
          // visitor just earned is deleted the moment it is stored. That is the exact path a
          // newsletter signup takes: the popup converts BEFORE anything is in the cart, so the code
          // was wiped on the very next reconcile and the customer saw full price. Cost us a
          // Saturday's contribution margin on 8/8/26 (DK cart -> checkout halved, 44% -> 21%).
          // The cookie path on the next branch has always had this guard; this one was missing it.
          clearStoredDiscountCode();
          changed = true;
        } else if (!storedCode && hadCachedPercent) {
          clearWelcomeDiscountCache();
          changed = true;
        }

        if (cookieCode && cart.item_count > 0 && !applicableCodes.includes(cookieCode) && !isWaitingBfCode(cookieCode)) {
          clearDiscountCookie();
          changed = true;
        }

        if (!localStorage.getItem(DISCOUNT_CODE_KEY) && cookieCode) {
          localStorage.setItem(DISCOUNT_CODE_KEY, cookieCode);
          changed = true;
        }
      } else {
        const activeCode = getPercentageCodeFromCart(cart);
        if (activeCode) {
          const nextPercent = String(Number(pctApp.value));
          const prevPercent = localStorage.getItem(CACHED_PERCENT_KEY);
          const prevCode = localStorage.getItem(CACHED_CODE_KEY);
          localStorage.setItem(CACHED_PERCENT_KEY, nextPercent);
          localStorage.setItem(CACHED_CODE_KEY, activeCode);
          if (storedCode !== activeCode) {
            localStorage.setItem(DISCOUNT_CODE_KEY, activeCode);
            changed = true;
          } else if (prevPercent !== nextPercent || prevCode !== activeCode) {
            changed = true;
          }
        }
      }

      if (changed) notifyCodeChange();
    } catch {
      /* cart fetch failed — leave storage as-is */
    }
  }

  // Migration from old SC app storage key
  function migrateFromScApp() {
    const oldCode = localStorage.getItem('sc:discount.code');
    if (oldCode && !localStorage.getItem(DISCOUNT_CODE_KEY)) {
      localStorage.setItem(DISCOUNT_CODE_KEY, oldCode.trim().toLowerCase());
      notifyCodeChange();
    }
  }

  function getStoredDiscountCode() {
    const localCode = localStorage.getItem(DISCOUNT_CODE_KEY)?.trim().toLowerCase() || '';
    if (localCode) return localCode;
    return getCookieDiscountCode();
  }

  /** Apply cookie/localStorage code to the cart (e.g. after Klaviyo popup). */
  async function applyStoredDiscountToCartTransaction({ refresh = true, code = getStoredDiscountCode() } = {}) {
    if (!code) return false;

    if (!localStorage.getItem(DISCOUNT_CODE_KEY)) {
      localStorage.setItem(DISCOUNT_CODE_KEY, code);
    }

    const cart = await fetch(`${getRoot()}cart.js`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    }).then((r) => r.json());

    if (isCodeApplied(cart, code)) {
      const pctApp = getPercentageApp(cart);
      if (pctApp && Number(pctApp.value) > 0) {
        localStorage.setItem(CACHED_PERCENT_KEY, String(Number(pctApp.value)));
        localStorage.setItem(CACHED_CODE_KEY, code);
      }
      notifyCodeChange();
      if (refresh) dispatchCartRefresh();
      return true;
    }

    const updated = await applyCode(cart, code);

    if (!isCodeApplied(updated, code)) return false;

    localStorage.setItem(DISCOUNT_CODE_KEY, code.toLowerCase());

    const pctApp = getPercentageApp(updated);
    if (pctApp && Number(pctApp.value) > 0) {
      localStorage.setItem(CACHED_PERCENT_KEY, String(Number(pctApp.value)));
      localStorage.setItem(CACHED_CODE_KEY, code.toLowerCase());
    }

    notifyCodeChange();
    if (refresh) dispatchCartRefresh();
    return true;
  }

  function applyStoredDiscountToCart(options) {
    const code = options?.code?.trim().toLowerCase();
    const stored = localStorage.getItem(DISCOUNT_CODE_KEY)?.trim().toLowerCase() || '';
    if (code && window.bfInsider?.isCode?.(stored) && !window.bfInsider.isCode(code)) {
      return Promise.resolve(false);
    }
    return changeDiscount(() => applyStoredDiscountToCartTransaction(options));
  }

  class CartDiscount extends HTMLElement {
    connectedCallback() {
      this.toggle = this.querySelector('.cart-discount__toggle');
      this.panel = this.querySelector('.cart-discount__panel');
      this.input = this.querySelector('.cart-discount__input');
      this.applyBtn = this.querySelector('.cart-discount__apply-btn');
      this.errorEl = this.querySelector('.cart-discount__error');
      this.tagsList = this.querySelector('.cart-discount__tags');

      this.toggle?.addEventListener('click', () => this.togglePanel());
      this.applyBtn?.addEventListener('click', () => this.apply());
      this.input?.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') this.apply();
      });

      this.bindRemoveButtons();

      if (panelWasOpen) {
        panelWasOpen = false;
        this.openPanel(false);
      }
    }

    bindRemoveButtons() {
      this.querySelectorAll('[data-remove-discount]').forEach((btn) => {
        btn.addEventListener('click', (e) => this.remove(e.currentTarget.dataset.removeDiscount));
      });
    }

    openPanel(focusInput = true) {
      if (!this.panel || !this.toggle) return;
      this.panel.hidden = false;
      this.toggle.setAttribute('aria-expanded', 'true');
      if (focusInput) {
        setTimeout(() => this.input?.focus(), 50);
      }
    }

    closePanel() {
      if (!this.panel || !this.toggle) return;
      this.panel.hidden = true;
      this.toggle.setAttribute('aria-expanded', 'false');
    }

    togglePanel() {
      if (!this.panel.hidden) {
        this.closePanel();
      } else {
        this.openPanel();
      }
    }

    async apply() {
      const code = this.input?.value.trim();
      if (!code || this.busy) return;

      this.setBusy(true);
      this.clearError();

      try {
        await changeDiscount(async () => {
          // Collect existing active codes so we can preserve them alongside the new one
          const currentCart = await fetch(`${getRoot()}cart.js`, {
            credentials: 'same-origin',
            headers: { Accept: 'application/json' },
          }).then((r) => r.json());

          const cart = await applyCode(currentCart, code);

          if (!isCodeApplied(cart, code)) {
            this.showError(this.dataset.tErrorInvalid || 'Invalid or inapplicable discount code.');
            dispatchCartRefresh();
            return;
          }

          localStorage.setItem(DISCOUNT_CODE_KEY, code.toLowerCase());

          // Cache percent immediately if available
          const pctApp = getPercentageApp(cart);
          if (pctApp && Number(pctApp.value) > 0) {
            localStorage.setItem(CACHED_PERCENT_KEY, String(Number(pctApp.value)));
            localStorage.setItem(CACHED_CODE_KEY, code.toLowerCase());
          }

          notifyCodeChange();
          this.input.value = '';
          this.updateTagsUI(cart);
          dispatchCartRefresh();
        });
      } catch {
        this.showError(this.dataset.tErrorGeneric || 'Something went wrong, please try again.');
      } finally {
        this.setBusy(false);
      }
    }

    async remove(codeToRemove) {
      if (this.busy) return;
      this.setBusy(true);
      this.clearError();

      try {
        await changeDiscount(async () => {
          // Fetch current codes so we can keep the others when removing one
          const currentCart = await fetch(`${getRoot()}cart.js`, {
            credentials: 'same-origin',
            headers: { Accept: 'application/json' },
          }).then((r) => r.json());

          const remaining = (currentCart.discount_codes || [])
            .map((d) => d.code)
            .filter((c) => c.toLowerCase() !== codeToRemove.toLowerCase());

          const discount = remaining.length > 0 ? remaining.join(',') : '';
          const cart = await postCartUpdate({ discount });

          // Clear localStorage only if the removed code was the stored one
          const storedCode = localStorage.getItem(DISCOUNT_CODE_KEY)?.toLowerCase();
          if (!storedCode || storedCode === codeToRemove.toLowerCase()) {
            clearStoredDiscountCode();
            notifyCodeChange();
          } else {
            clearWelcomeDiscountCache();
            notifyCodeChange();
          }

          if (getCookieDiscountCode() === codeToRemove.toLowerCase()) {
            clearDiscountCookie();
          }

          this.updateTagsUI(cart);
          dispatchCartRefresh();
        });
      } catch {
        this.showError(this.dataset.tErrorGeneric || 'Something went wrong, please try again.');
      } finally {
        this.setBusy(false);
      }
    }

    updateTagsUI(cart) {
      if (!this.tagsList) return;

      const tags = getAppliedDiscountTags(cart);
      const removeTpl = this.dataset.tRemove || 'Remove %code%';

      this.tagsList.innerHTML = tags
        .map((d) => {
          if (d.removable) {
            return `
          <li class="cart-discount__tag">
            <span class="cart-discount__tag-label">${d.title}</span>
            <button type="button" class="cart-discount__tag-remove" data-remove-discount="${d.title}" aria-label="${removeTpl.replace('%code%', d.title)}">
              <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M18 6L6 18M6 6l12 12"/></svg>
            </button>
          </li>`;
          }
          return `
          <li class="cart-discount__tag cart-discount__tag--automatic">
            <span class="cart-discount__tag-label">${d.title}</span>
          </li>`;
        })
        .join('');

      this.bindRemoveButtons();
      this.updateToggleLabel(tags.length);
    }

    updateToggleLabel(benefitTagCount) {
      const label = this.querySelector('.cart-discount__toggle-label');
      const seeMoreText = this.querySelector('.cart-discount__see-more-text');
      const freeShippingBenefit = Number(this.dataset.freeShippingBenefit) || 0;
      const adventBundleBenefit = Number(this.dataset.adventBundleBenefit) || 0;
      const count = benefitTagCount + freeShippingBenefit + adventBundleBenefit;
      if (label) {
        const tpl = count === 1
          ? (this.dataset.tCountOne || '%count% discount active')
          : (this.dataset.tCountOther || '%count% discounts active');
        label.textContent = count > 0
          ? tpl.replace('%count%', count)
          : (this.dataset.tLabel || 'Discount codes');
      }
      if (seeMoreText) {
        seeMoreText.textContent = count > 0
          ? (this.dataset.tSeeMoreActive || 'See discounts')
          : (this.dataset.tSeeMoreEmpty || 'Enter code');
      }
    }

    setBusy(busy) {
      this.busy = busy;
      if (this.applyBtn) {
        this.applyBtn.disabled = busy;
        this.applyBtn.textContent = busy ? '...' : (this.dataset.tApply || 'Apply');
      }
    }

    clearError() {
      if (this.errorEl) {
        this.errorEl.hidden = true;
        this.errorEl.textContent = '';
      }
    }

    showError(msg) {
      if (this.errorEl) {
        this.errorEl.textContent = msg;
        this.errorEl.hidden = false;
      }
    }
  }

  if (!customElements.get('cart-discount')) {
    customElements.define('cart-discount', CartDiscount);
  }

  /* ------------------------------------------------------------------------------------------
     Share one reconcile between simultaneous callers.

     Every <welcome-discount-price> element calls reconcileDiscountState() from its own
     connectedCallback, and each call fetches cart.js. A product page carries 31 of them, so the
     page opened 14 identical cart.js requests — 8.4s of cumulative fetch time, the last landing
     4.2s in, which is squarely inside the INP measurement window on mobile (359 URLs flagged).

     🚨 This de-duplication was shipped once before (33baea3) and reverted hours later (b695517)
     because the Danish welcome discount stopped applying. It was NOT the cause. The cause was the
     missing `cart.item_count > 0` guard above: an empty cart wiped the stored code on every
     reconcile. Running 31 reconciles hid it — a later pass restored the code from the cookie
     (see the cookieCode branch), so the damage was usually repaired before anyone saw it. Sharing
     one reconcile removed the accidental repair and made the real bug visible. With the guard
     fixed, the code is never wrongly cleared, and sharing the call is safe.

     Only concurrent callers share. The promise is released as soon as it settles, so anything
     triggered afterwards — cart:refresh, a variant change, applying a code — always reads a fresh
     cart. That is the difference from a cache, and the reason a stale read cannot happen.
     ------------------------------------------------------------------------------------------ */
  let reconcileInFlight = null;
  function reconcileDiscountStateShared() {
    if (reconcileInFlight) return reconcileInFlight;
    reconcileInFlight = Promise.resolve(reconcileDiscountState()).finally(() => {
      reconcileInFlight = null;
    });
    return reconcileInFlight;
  }

  document.addEventListener('cart:refresh', capturePanelOpenState, true);
  document.addEventListener('cart:refresh', () => {
    reconcileDiscountStateShared();
  });

  migrateFromScApp();
  window.reconcileCartDiscountState = reconcileDiscountStateShared;
  window.applyStoredDiscountToCart = applyStoredDiscountToCart;
  reconcileDiscountStateShared();
})();
